import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./AlertSvuotamentiCampane.css";
import RotateDevice from "../components/RotateDevice/RotateDevice";
import Lf23Loader from "../components/Lf23Loader";

const AlertSvuotamentiCampane = () => {
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showFilters, setShowFilters] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [showStoricoModal, setShowStoricoModal] = useState(false);
    const [selectedCampana, setSelectedCampana] = useState(null);
    const [expandedComuni, setExpandedComuni] = useState([]);
    const [viewMode, setViewMode] = useState("table"); // table o cards
    const [searchTerm, setSearchTerm] = useState("");
    const [fullscreenComune, setFullscreenComune] = useState(null);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape' && fullscreenComune) setFullscreenComune(null);
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [fullscreenComune]);

    // KPI Stats
    const [stats, setStats] = useState({
        totalComuni: 0,
        totalCampane: 0,
        campaneInAlert: 0,
        percentualeAlert: 0
    });

    // Filtri
    const [searchComune, setSearchComune] = useState("");
    const [filterAlert, setFilterAlert] = useState("alert"); // all, alert, no-alert

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchData = async () => {
        try {
            const response = await fetch(`${API_URL}/alert-svuotamenti-campane`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });
            const result = await response.json();
            setData(result);

            // Calcola statistiche KPI
            const comuniUniche = new Set(result.map(item => item.comune));
            const totalComuni = comuniUniche.size;
            const totalCampane = result.length;
            const campaneInAlert = result.filter(item => item.alert_campana).length;
            const percentualeAlert = totalCampane > 0 ? (campaneInAlert / totalCampane) * 100 : 0;

            setStats({
                totalComuni,
                totalCampane,
                campaneInAlert,
                percentualeAlert
            });
        } catch (error) {
            console.error("Errore nel caricamento dei dati:", error);
        } finally {
            setLoading(false);
        }
    };

    // Raggruppa i dati per comune
    const groupedData = React.useMemo(() => {
        const grouped = {};
        data.forEach(item => {
            if (!grouped[item.comune]) {
                grouped[item.comune] = {
                    comune: item.comune,
                    campane_totali: item.campane_totali_comune,
                    campane: []
                };
            }
            grouped[item.comune].campane.push(item);
        });
        return Object.values(grouped);
    }, [data]);

    // Filtra i comuni
    const filteredComuni = React.useMemo(() => {
        return groupedData.filter(gruppo => {
            // Filtro per nome comune (con search term generale)
            if (searchComune && !gruppo.comune.toLowerCase().includes(searchComune.toLowerCase())) {
                return false;
            }

            // Filtro per searchTerm generale (cerca anche in indirizzi e tag)
            if (searchTerm) {
                const searchLower = searchTerm.toLowerCase();
                const matchComune = gruppo.comune.toLowerCase().includes(searchLower);
                const matchCampana = gruppo.campane.some(c =>
                    c.indirizzo?.toLowerCase().includes(searchLower) ||
                    c.tag?.toLowerCase().includes(searchLower)
                );
                if (!matchComune && !matchCampana) return false;
            }

            // Filtro per alert
            if (filterAlert === "alert") {
                const hasAlert = gruppo.campane.some(c => c.alert_campana);
                if (!hasAlert) return false;
            } else if (filterAlert === "no-alert") {
                const hasAlert = gruppo.campane.some(c => c.alert_campana);
                if (hasAlert) return false;
            }

            return true;
        });
    }, [groupedData, searchComune, searchTerm, filterAlert]);

    const toggleComune = (comune) => {
        setExpandedComuni(prev =>
            prev.includes(comune)
                ? prev.filter(c => c !== comune)
                : [...prev, comune]
        );
    };

    const openStoricoModal = (campana) => {
        setFullscreenComune(null);
        setSelectedCampana(campana);
        setShowStoricoModal(true);
    };

    const getStoricoSvuotamenti = (storicoData) => {
        // PostgreSQL restituisce i campi JSON già come oggetti
        if (typeof storicoData === 'string') {
            try {
                return JSON.parse(storicoData);
            } catch (e) {
                console.error('Errore nel parsing dello storico:', e);
                return [];
            }
        }
        return Array.isArray(storicoData) ? storicoData : [];
    };

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        return date.toLocaleDateString("it-IT", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    const formatDateOnly = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        return date.toLocaleDateString("it-IT", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric"
        });
    };

    const isScadenzaVicina = (prossima_scadenza) => {
        if (!prossima_scadenza) return false;
        const oggi = new Date();
        const scadenza = new Date(prossima_scadenza);
        const diffDays = Math.ceil((scadenza - oggi) / (1000 * 60 * 60 * 24));
        return diffDays <= 3; // Scadenza entro 3 giorni
    };

    return (
        <div className="alert-campane-container">
            {/* Header */}
            <header className="page-header">
                <div className="header-content">
                    <button className="back-button" onClick={() => navigate("/produttivita")}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                        Indietro
                    </button>
                    <div className="header-title">
                        <div className="header-title-row">
                            <h2>Alert Svuotamenti Campane</h2>
                            <button
                                className="info-button-modern"
                                onClick={() => setShowInfoModal(true)}
                                title="Informazioni"
                            >
                                <span className="info-icon-text">i</span>
                            </button>
                        </div>
                        <p className="header-subtitle">Monitoraggio e allerta campane da svuotare per comune</p>
                    </div>
                </div>
            </header>

            {/* Modal Info */}
            {showInfoModal && (
                <div className="modal-overlay" onClick={() => setShowInfoModal(false)}>
                    <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="modal-icon">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                                    <path d="M10 8h6"></path>
                                    <path d="M10 12h6"></path>
                                    <path d="M10 16h4"></path>
                                </svg>
                            </div>
                            <h3>Informazioni sulla pagina</h3>
                            <button className="modal-close" onClick={() => setShowInfoModal(false)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="modal-section">
                                <h4>Cosa fa questa pagina?</h4>
                                <p>
                                    Questa dashboard permette di monitorare lo stato delle campane per la raccolta differenziata,
                                    fornendo <strong>alert sulle campane che necessitano di svuotamento</strong> in base alle scadenze programmate
                                    e allo storico degli svuotamenti effettuati.
                                </p>
                            </div>
                            <div className="modal-section">
                                <h4>KPI Comuni</h4>
                                <ul>
                                    <li><strong>Totale Comuni:</strong> Numero di comuni gestiti nel sistema</li>
                                    <li><strong>Totale Campane:</strong> Numero complessivo di campane monitorate</li>
                                    <li><strong>Campane in Alert:</strong> Campane che necessitano svuotamento urgente</li>
                                    <li><strong>% Alert:</strong> Percentuale di campane in stato di allerta</li>
                                </ul>
                            </div>
                            <div className="modal-section">
                                <h4>Dettagli Campane</h4>
                                <ul>
                                    <li><strong>Svuotamento Medio:</strong> Percentuale media di riempimento durante gli svuotamenti</li>
                                    <li><strong>Ultimo Svuotamento:</strong> Data e ora dell'ultimo svuotamento effettuato</li>
                                    <li><strong>Prossima Scadenza:</strong> Data prevista per il prossimo svuotamento</li>
                                    <li><strong>Storico:</strong> Cronologia completa degli svuotamenti con driver e percentuali</li>
                                </ul>
                                <p className="modal-tip">
                                    💡 Utilizza i filtri per raffinare la ricerca per comune o stato di allerta.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Content Area */}
            <div className="content-area">
                {/* Top Controls */}
                <div className="top-controls">
                    <div className="controls-left">
                        <button
                            className={`toggle-filters-btn ${showFilters ? 'active' : ''}`}
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                            </svg>
                            {showFilters ? 'Nascondi Filtri' : 'Mostra Filtri'}
                        </button>

                        <div className="search-bar-compact">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"></circle>
                                <path d="m21 21-4.35-4.35"></path>
                            </svg>
                            <input
                                type="text"
                                placeholder="Cerca comune, indirizzo o tag..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="controls-right">
                        <div className="view-toggle-compact">
                            <button
                                className={`view-btn-compact ${viewMode === 'table' ? 'active' : ''}`}
                                onClick={() => setViewMode('table')}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M3 9h18M3 15h18M9 3v18"></path>
                                </svg>
                                Tabella
                            </button>
                            <button
                                className={`view-btn-compact ${viewMode === 'cards' ? 'active' : ''}`}
                                onClick={() => setViewMode('cards')}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="3" width="7" height="7"></rect>
                                    <rect x="14" y="3" width="7" height="7"></rect>
                                    <rect x="14" y="14" width="7" height="7"></rect>
                                    <rect x="3" y="14" width="7" height="7"></rect>
                                </svg>
                                Cards
                            </button>
                        </div>
                    </div>
                </div>

                {/* Filters Panel */}
                {showFilters && (
                    <div className="filters-panel">
                        <div className="filters-grid">
                            <div className="filter-item">
                                <label>Cerca Comune</label>
                                <input
                                    type="text"
                                    placeholder="Digita il nome del comune..."
                                    value={searchComune}
                                    onChange={(e) => setSearchComune(e.target.value)}
                                />
                            </div>
                            <div className="filter-item">
                                <label>Stato Alert</label>
                                <select
                                    value={filterAlert}
                                    onChange={(e) => setFilterAlert(e.target.value)}
                                >
                                    <option value="all">Tutti</option>
                                    <option value="alert">Con Alert</option>
                                    <option value="no-alert">Senza Alert</option>
                                </select>
                            </div>
                        </div>
                    </div>
                )}

                {/* KPI Stats Grid */}
                <div className="stats-grid-compact">
                    <div className="stat-card-compact stat-primary">
                        <div className="stat-icon-compact">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                                <polyline points="9 22 9 12 15 12 15 22"></polyline>
                            </svg>
                        </div>
                        <div className="stat-content-compact">
                            <p className="stat-label-compact">Totale Comuni</p>
                            <p className="stat-value-compact">{stats.totalComuni}</p>
                        </div>
                    </div>

                    <div className="stat-card-compact stat-success">
                        <div className="stat-icon-compact">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"></circle>
                                <path d="M12 16v-4"></path>
                                <path d="M12 8h.01"></path>
                            </svg>
                        </div>
                        <div className="stat-content-compact">
                            <p className="stat-label-compact">Totale Campane</p>
                            <p className="stat-value-compact">{stats.totalCampane}</p>
                        </div>
                    </div>

                    <div className="stat-card-compact stat-danger">
                        <div className="stat-icon-compact">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                                <line x1="12" y1="9" x2="12" y2="13"></line>
                                <line x1="12" y1="17" x2="12.01" y2="17"></line>
                            </svg>
                        </div>
                        <div className="stat-content-compact">
                            <p className="stat-label-compact">Campane in Alert</p>
                            <p className="stat-value-compact">{stats.campaneInAlert}</p>
                        </div>
                    </div>

                    <div className="stat-card-compact stat-warning">
                        <div className="stat-icon-compact">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="20" x2="18" y2="10"></line>
                                <line x1="12" y1="20" x2="12" y2="4"></line>
                                <line x1="6" y1="20" x2="6" y2="14"></line>
                            </svg>
                        </div>
                        <div className="stat-content-compact">
                            <p className="stat-label-compact">% Alert</p>
                            <p className="stat-value-compact">{stats.percentualeAlert.toFixed(1)}%</p>
                        </div>
                    </div>
                </div>

                {/* Lista Comuni */}
                {loading ? (
                    <Lf23Loader />
                ) : viewMode === 'table' ? (
                    <div className="comuni-list">
                        {filteredComuni.map(gruppo => {
                            const campaneConAlert = gruppo.campane.filter(c => c.alert_campana).length;
                            const isExpanded = expandedComuni.includes(gruppo.comune);

                            return (
                                <div key={gruppo.comune} className="comune-card">
                                    <div
                                        className="comune-header"
                                        onClick={() => toggleComune(gruppo.comune)}
                                    >
                                        <div className="comune-info">
                                            <h3>{gruppo.comune}</h3>
                                            <div className="comune-stats">
                                                <span className="stat-item">
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <circle cx="12" cy="12" r="10"></circle>
                                                    </svg>
                                                    {gruppo.campane_totali} campane totali
                                                </span>
                                                {campaneConAlert > 0 && (
                                                    <span className="stat-item alert-badge-header">
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                                                            <line x1="12" y1="9" x2="12" y2="13"></line>
                                                            <line x1="12" y1="17" x2="12.01" y2="17"></line>
                                                        </svg>
                                                        {campaneConAlert} in alert
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <svg
                                            className={`expand-icon ${isExpanded ? 'expanded' : ''}`}
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <polyline points="6 9 12 15 18 9"></polyline>
                                        </svg>
                                    </div>

                                    {isExpanded && (
                                        <div className={`campane-table-container${fullscreenComune === gruppo.comune ? ' fullscreen-table-alert' : ''}`}>
                                            <RotateDevice active={fullscreenComune === gruppo.comune} />
                                            {fullscreenComune === gruppo.comune && (
                                                <div className="fullscreen-exit-bar-alert">
                                                    <span className="fullscreen-title-alert">{gruppo.comune}</span>
                                                    <button
                                                        className="fullscreen-btn-alert"
                                                        onClick={() => setFullscreenComune(null)}
                                                        title="Esci da tutto schermo"
                                                    >
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <polyline points="4 14 10 14 10 20"></polyline>
                                                            <polyline points="20 10 14 10 14 4"></polyline>
                                                            <line x1="14" y1="10" x2="21" y2="3"></line>
                                                            <line x1="3" y1="21" x2="10" y2="14"></line>
                                                        </svg>
                                                    </button>
                                                </div>
                                            )}
                                            <div className="fullscreen-btn-row-alert">
                                                <button
                                                    className="fullscreen-btn-alert"
                                                    onClick={() => setFullscreenComune(fullscreenComune === gruppo.comune ? null : gruppo.comune)}
                                                    title={fullscreenComune === gruppo.comune ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        {fullscreenComune === gruppo.comune ? (
                                                            <>
                                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <polyline points="15 3 21 3 21 9"></polyline>
                                                                <polyline points="9 21 3 21 3 15"></polyline>
                                                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                                            </>
                                                        )}
                                                    </svg>
                                                </button>
                                            </div>
                                            <table className="campane-table">
                                                <thead>
                                                    <tr>
                                                        <th>Indirizzo</th>
                                                        <th>Tag</th>
                                                        <th className="numeric-cell">Svuot. Medio %</th>
                                                        <th>Ultimo Svuotamento</th>
                                                        <th>Prossima Scadenza</th>
                                                        <th>Storico</th>
                                                        <th className="center-cell">Stato</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {gruppo.campane.map((campana, idx) => (
                                                        <tr key={idx} className={campana.alert_campana ? 'alert-row' : ''}>
                                                            <td>{campana.indirizzo}</td>
                                                            <td><code>{campana.tag}</code></td>
                                                            <td className="numeric-cell">
                                                                <span className="percentage-badge">
                                                                    {parseFloat(campana.svuotamento_medio).toFixed(1)}%
                                                                </span>
                                                            </td>
                                                            <td>{formatDate(campana.ultima_data_svuotamento)}</td>
                                                            <td>
                                                                <span className={`scadenza-badge ${isScadenzaVicina(campana.prossima_scadenza) ? 'vicina' : ''}`}>
                                                                    {formatDateOnly(campana.prossima_scadenza)}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <button
                                                                    className="btn-storico"
                                                                    onClick={() => openStoricoModal(campana)}
                                                                >
                                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                                                        <polyline points="14 2 14 8 20 8"></polyline>
                                                                        <line x1="16" y1="13" x2="8" y2="13"></line>
                                                                        <line x1="16" y1="17" x2="8" y2="17"></line>
                                                                    </svg>
                                                                    Vedi Storico
                                                                </button>
                                                            </td>
                                                            <td className="center-cell">
                                                                {campana.alert_campana ? (
                                                                    <span className="status-badge alert">
                                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                                                            <circle cx="12" cy="12" r="10"></circle>
                                                                        </svg>
                                                                        Alert
                                                                    </span>
                                                                ) : (
                                                                    <span className="status-badge ok">
                                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                                                            <circle cx="12" cy="12" r="10"></circle>
                                                                        </svg>
                                                                        OK
                                                                    </span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    /* Cards View */
                    <div className="cards-grid-campane">
                        {filteredComuni.map(gruppo => {
                            const campaneConAlert = gruppo.campane.filter(c => c.alert_campana).length;

                            return (
                                <div key={gruppo.comune} className="comune-card-view">
                                    <div className="card-header-campane">
                                        <h3>{gruppo.comune}</h3>
                                        {campaneConAlert > 0 && (
                                            <span className="alert-badge-card">
                                                {campaneConAlert} Alert
                                            </span>
                                        )}
                                    </div>
                                    <div className="card-stats-campane">
                                        <div className="stat-row">
                                            <span className="stat-label">Totale Campane:</span>
                                            <span className="stat-value-card">{gruppo.campane_totali}</span>
                                        </div>
                                        <div className="stat-row">
                                            <span className="stat-label">Campane in Alert:</span>
                                            <span className="stat-value-card alert-text">{campaneConAlert}</span>
                                        </div>
                                    </div>
                                    <div className="card-campane-list">
                                        {gruppo.campane.slice(0, 3).map((campana, idx) => (
                                            <div key={idx} className={`mini-campana ${campana.alert_campana ? 'alert' : ''}`}>
                                                <div className="mini-campana-info">
                                                    <strong>{campana.indirizzo}</strong>
                                                    <span className="mini-tag">{campana.tag}</span>
                                                </div>
                                                {campana.alert_campana && (
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="alert-icon-mini">
                                                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                                                    </svg>
                                                )}
                                            </div>
                                        ))}
                                        {gruppo.campane.length > 3 && (
                                            <div className="more-campane">
                                                +{gruppo.campane.length - 3} altre campane
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        className="btn-view-details"
                                        onClick={() => {
                                            setViewMode('table');
                                            toggleComune(gruppo.comune);
                                        }}
                                    >
                                        Vedi Dettagli
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="9 18 15 12 9 6"></polyline>
                                        </svg>
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Modal Storico Svuotamenti - Stile Moderno */}
            {showStoricoModal && selectedCampana && (
                <div className="modal-overlay-dettaglio" onClick={() => setShowStoricoModal(false)}>
                    <div className="modal-container-dettaglio" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-dettaglio">
                            <div className="modal-icon-dettaglio">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M3 6h18"></path>
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
                                    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                    <line x1="10" y1="11" x2="10" y2="17"></line>
                                    <line x1="14" y1="11" x2="14" y2="17"></line>
                                </svg>
                            </div>
                            <div>
                                <h3>Storico Svuotamenti</h3>
                                <p className="modal-subtitle-dettaglio">{selectedCampana.indirizzo} - {selectedCampana.tag}</p>
                            </div>
                            <button className="modal-close-dettaglio" onClick={() => setShowStoricoModal(false)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body-dettaglio">
                            <table className="dettaglio-table">
                                <thead>
                                    <tr>
                                        <th>Data Svuotamento</th>
                                        <th>Driver</th>
                                        <th className="numeric-cell">Percentuale</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getStoricoSvuotamenti(selectedCampana.storico_svuotamenti)
                                        .sort((a, b) => {
                                            const dateA = new Date(a.data_svuotamento);
                                            const dateB = new Date(b.data_svuotamento);
                                            return dateB - dateA; // Ordine decrescente (più recente prima)
                                        })
                                        .map((item, idx) => (
                                            <tr key={idx}>
                                                <td>{formatDate(item.data_svuotamento)}</td>
                                                <td>{item.driver_svuotamento || <em style={{color: '#94A3B8'}}>Non specificato</em>}</td>
                                                <td className="numeric-cell">
                                                    <span className="percentage-badge">
                                                        {item.percentuale_svuotamento}%
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AlertSvuotamentiCampane;
